import React, { useEffect } from 'react';

import { Box, Button, Grid } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { ReactHookFormInput } from 'common/components/form/bootstrap';
import ShowErrors from 'common/components/ShowErrors';

import * as actions from '../actions';
import * as selectors from '../selectors';
import validate from '../validate';

const FormContainer = ({
    errors,
    filterData,
    handleClickCloseForm,
    handleClickRestartPassword,
    initialValues,
    isNewRecord,
    isSaving,
    save,
    setDialogTitle,
}) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, getValues } = useForm({
        context: { isNewRecord },
        defaultValues: initialValues,
        resolver: validate,
    });

    const id = getValues('id');
    const isOwner = getValues('isOwner');
    const isDisabled = !isNewRecord && isOwner;

    useEffect(() => {
        reset(initialValues);
    }, [initialValues]);

    useEffect(() => {
        setDialogTitle(
            `${isNewRecord ? t('Add') : t('Update')} ${t('User').toLowerCase()}`,
        );
    }, [isNewRecord]);

    const handleClickSave = (values) => save(filterData, values);

    return (
        <form>
            <ReactHookFormInput
                control={control}
                disabled={isDisabled}
                label={t('Name').concat(' *')}
                name="first_name"
            />

            <ReactHookFormInput
                control={control}
                disabled={isDisabled}
                label={t('Last name').concat(' *')}
                name="last_name"
            />

            <ReactHookFormInput
                control={control}
                disabled={isDisabled}
                label={t('Phone number')}
                name="phone"
            />

            <ReactHookFormInput
                control={control}
                disabled={!isNewRecord || isDisabled}
                label={t('Email').concat(' *')}
                name="email"
            />

            {isNewRecord && (
                <>
                    <ReactHookFormInput
                        control={control}
                        disabled={isDisabled}
                        label={t('Password').concat(' *')}
                        name="password"
                        type="password"
                    />

                    <ReactHookFormInput
                        control={control}
                        disabled={isDisabled}
                        label={t('Confirm password').concat(' *')}
                        name="confirm_password"
                        type="password"
                    />
                </>
            )}

            <ShowErrors errors={errors} />

            <Grid container mt={1}>
                <Grid
                    size="grow"
                    sx={{
                        display: 'flex',
                        flexDirection: { md: 'row', xs: 'column' },
                    }}
                >
                    {!isNewRecord && (
                        <Button
                            onClick={() => handleClickRestartPassword(id)}
                            disabled={isSaving}
                            sx={{
                                mb: { md: 0, xs: 2 },
                                width: { md: 'auto', xs: '100%' },
                            }}
                            type="button"
                            variant="text"
                        >
                            {t('Restore password')}
                        </Button>
                    )}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { md: 'row', xs: 'column' },
                            gap: '16px',
                            ml: 'auto',
                            width: { md: 'auto', xs: '100%' },
                        }}
                    >
                        <Button
                            onClick={handleClickCloseForm}
                            sx={{
                                mr: { md: 2, xs: 0 },
                                order: { md: 1, xs: 2 },
                                width: { md: 'auto', xs: '100%' },
                            }}
                            type="button"
                            variant="text"
                        >
                            {isNewRecord ? t('Cancel') : t('Close')}
                        </Button>
                        {isNewRecord && (
                            <Button
                                onClick={handleSubmit(handleClickSave)}
                                disabled={isSaving}
                                sx={{
                                    mb: { md: 0, xs: 2 },
                                    order: { md: 2, xs: 1 },
                                    width: { md: 'auto', xs: '100%' },
                                }}
                                type="button"
                                variant="outlined"
                            >
                                {t('Save')}
                            </Button>
                        )}
                        {!isNewRecord && !isDisabled && (
                            <Button
                                disabled={isSaving}
                                onClick={handleSubmit(handleClickSave)}
                                sx={{
                                    mb: { md: 0, xs: 2 },
                                    order: { md: 3, xs: 1 },
                                    width: { md: 'auto', xs: '100%' },
                                }}
                                type="button"
                                variant="outlined"
                            >
                                {t('Save')}
                            </Button>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </form>
    );
};

const mapStateToProps = createStructuredSelector({
    initialValues: selectors.getInitialValues,
    isNewRecord: selectors.getIsNewRecord,
    isSaving: selectors.getIsSavingItem,
});

const mapDispatchToProps = (dispatch) => ({
    handleClickRestartPassword: (id) =>
        dispatch(actions.prepareResetPassword(id)),
    save: (filterData, values) => dispatch(actions.save(filterData, values)),
});

FormContainer.propTypes = {
    errors: PropTypes.object,
    filterData: PropTypes.object,
    handleClickCloseForm: PropTypes.func,
    handleClickRestartPassword: PropTypes.func,
    initialValues: PropTypes.object,
    isNewRecord: PropTypes.bool,
    isSaving: PropTypes.bool,
    save: PropTypes.func,
    setDialogTitle: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormContainer);
